import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../hooks/useAuth";
import { useLocalProgress } from "../hooks/useLocalProgress";
import { useOutsideClick } from "../hooks/useOutsideClick";
import { GoogleOneTap, renderGoogleButton } from "./GoogleOneTap";
import { StreakPopup } from "./StreakPopup";
import { DifficultyBadge } from "./DifficultyBadge";
import type { Difficulty } from "../types";
import styles from "./Header.module.css";

interface HeaderProps {
  date: string;
  size: "7x7" | "9x9";
  difficulty?: Difficulty;
  showDate?: boolean;
  streakBadgeRef?: React.RefObject<HTMLButtonElement | null>;
  onSizeChange: (size: "7x7" | "9x9") => void;
  onHowToPlay: () => void;
  onNewGame: () => void;
  onNewGameWithDifficulty?: (difficulty: Difficulty) => void;
  onDaily: () => void;
  onArchive: () => void;
  onStats: () => void;
  onLeaderboard: () => void;
}

function UserAvatar() {
  const { user, logout } = useAuth();
  const [showMenu, setShowMenu] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const closeMenu = useCallback(() => setShowMenu(false), []);
  useOutsideClick(wrapRef, showMenu, closeMenu);

  if (!user) return null;

  const initial = (user.displayName ?? user.email)[0].toUpperCase();

  return (
    <div className={styles.avatarWrap} ref={wrapRef}>
      <button
        className={styles.avatar}
        onClick={() => setShowMenu((v) => !v)}
        aria-label="Account menu"
        title={user.displayName ?? user.email}
      >
        {user.avatarUrl ? (
          <img src={user.avatarUrl} alt="" className={styles.avatarImg} referrerPolicy="no-referrer" />
        ) : (
          initial
        )}
      </button>
      {showMenu && (
        <div className={styles.dropdown}>
          <div className={styles.dropdownName}>{user.displayName}</div>
          <div className={styles.dropdownEmail}>{user.email}</div>
          <button className={styles.dropdownItem} onClick={() => { setShowMenu(false); logout(); }}>
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

function LoginButton() {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const btnContainerRef = useRef<HTMLDivElement>(null);

  const closeLogin = useCallback(() => setOpen(false), []);
  useOutsideClick(wrapRef, open, closeLogin);

  useEffect(() => {
    if (open && btnContainerRef.current) {
      renderGoogleButton(btnContainerRef.current);
    }
  }, [open]);

  return (
    <div className={styles.avatarWrap} ref={wrapRef}>
      <button
        className={styles.iconButton}
        onClick={() => setOpen((v) => !v)}
        aria-label="Sign in"
        title="Sign in"
      >
        &#9786;
      </button>
      {open && (
        <div className={styles.dropdown}>
          <div className={styles.dropdownName}>Sign in</div>
          <div style={{ padding: "8px 16px" }}>
            <div ref={btnContainerRef} />
          </div>
        </div>
      )}
    </div>
  );
}

export function Header({ date, size, difficulty, showDate = true, streakBadgeRef, onSizeChange, onHowToPlay, onNewGame, onNewGameWithDifficulty, onDaily, onArchive, onStats, onLeaderboard }: HeaderProps) {
  const { user, loading } = useAuth();
  const { getPlayStreak } = useLocalProgress();
  const streak = getPlayStreak(size);
  const [showStreak, setShowStreak] = useState(false);
  const [showOverflow, setShowOverflow] = useState(false);
  const overflowRef = useRef<HTMLDivElement>(null);

  const closeOverflow = useCallback(() => setShowOverflow(false), []);
  useOutsideClick(overflowRef, showOverflow, closeOverflow);

  const isRandom = date === "random";
  const dateObj = isRandom ? null : new Date(date + "T00:00:00Z");
  const formattedDateLong = isRandom
    ? "Random Puzzle"
    : dateObj!.toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
        timeZone: "UTC",
      });
  const formattedDateShort = isRandom
    ? "Random Puzzle"
    : dateObj!.toLocaleDateString("en-US", {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "numeric",
        timeZone: "UTC",
      });

  return (
    <header className={styles.header}>
      <div className={styles.left}>
        <div className={styles.overflowWrap} ref={overflowRef}>
          <button
            className={`${styles.iconButton} ${styles.overflowButton}`}
            onClick={() => setShowOverflow((v) => !v)}
            aria-label="More options"
            title="More options"
          >
            &#8943;
          </button>
          {showOverflow && (
            <div className={styles.overflowMenu}>
              <button className={styles.dropdownItem} onClick={() => { setShowOverflow(false); onHowToPlay(); }}>
                <span className={styles.overflowIcon}>?</span> How to play
              </button>
              <button className={styles.dropdownItem} onClick={() => { setShowOverflow(false); onArchive(); }}>
                <span className={styles.overflowIcon}>&#9776;</span> Archive
              </button>
              <button className={styles.dropdownItem} onClick={() => { setShowOverflow(false); onStats(); }}>
                <span className={styles.overflowIcon}>&#9636;</span> Stats
              </button>
              <button className={styles.dropdownItem} onClick={() => { setShowOverflow(false); onLeaderboard(); }}>
                <span className={styles.overflowIcon}>&#9819;</span> Leaderboard
              </button>
              <button className={styles.dropdownItem} onClick={() => { setShowOverflow(false); onNewGame(); }}>
                <span className={styles.overflowIcon}>+</span> New random game
              </button>
            </div>
          )}
        </div>
        <button
          className={`${styles.iconButton} ${styles.hideNarrow}`}
          onClick={onHowToPlay}
          aria-label="How to play"
          title="How to play"
        >
          ?
        </button>
        <button
          className={`${styles.iconButton} ${styles.hideNarrow}`}
          onClick={onArchive}
          aria-label="Archive"
          title="Archive"
        >
          &#9776;
        </button>
        {streak.current > 0 && (
          <button
            ref={streakBadgeRef}
            className={styles.streakBadge}
            onClick={() => setShowStreak(true)}
            aria-label={`${streak.current} day streak`}
            title={`${streak.current} day streak${streak.pausedDays > 0 ? " (paused)" : ""}`}
          >
            <span className={streak.pausedDays > 0 ? styles.streakIconPaused : styles.streakIcon}>{streak.pausedDays > 0 ? "||" : "\uD83D\uDD25"}</span>
            <span className={styles.streakCount}>{streak.current}</span>
          </button>
        )}
      </div>
      <div className={styles.center}>
        <h1 className={styles.title}>
          <button
            className={styles.titleButton}
            onClick={onDaily}
            title="Today's puzzle"
          >
            Griddle
          </button>
        </h1>
        <p className={styles.date} style={showDate ? undefined : { visibility: "hidden" }}>
          <span className={styles.dateLong}>{formattedDateLong}</span>
          <span className={styles.dateShort}>{formattedDateShort}</span>
          {showDate && difficulty && (
            <DifficultyBadge
              difficulty={difficulty}
              isRandom={isRandom}
              onSelectDifficulty={isRandom ? onNewGameWithDifficulty : undefined}
            />
          )}
        </p>
        <div className={styles.sizePicker}>
          <button
            className={`${styles.sizeButton} ${size === "7x7" ? styles.sizeActive : ""}`}
            onClick={() => onSizeChange("7x7")}
            aria-pressed={size === "7x7"}
          >
            7x7
          </button>
          <button
            className={`${styles.sizeButton} ${size === "9x9" ? styles.sizeActive : ""}`}
            onClick={() => onSizeChange("9x9")}
            aria-pressed={size === "9x9"}
          >
            9x9
          </button>
        </div>
      </div>
      <div className={styles.right}>
        <button
          className={`${styles.iconButton} ${styles.hideNarrow}`}
          onClick={onStats}
          aria-label="Stats"
          title="Stats"
        >
          &#9636;
        </button>
        <button
          className={`${styles.iconButton} ${styles.hideNarrow}`}
          onClick={onLeaderboard}
          aria-label="Leaderboard"
          title="Leaderboard"
        >
          &#9819;
        </button>
        <button
          className={`${styles.iconButton} ${styles.hideNarrow}`}
          onClick={onNewGame}
          aria-label="New random game"
          title="New random game"
        >
          +
        </button>
        {user ? (
          <UserAvatar />
        ) : loading ? null : (
          <LoginButton />
        )}
      </div>
      <GoogleOneTap />
      {showStreak && (
        <StreakPopup
          current={streak.current}
          pausedDays={streak.pausedDays}
          recentDays={streak.recentDays}
          onClose={() => setShowStreak(false)}
        />
      )}
    </header>
  );
}
